import { getAdditionalUserInfo } from "firebase/auth";
import {
  userHasGoogleProvider,
  userHasPasswordProvider,
} from "../../../repositories/authRepository";
import { fetchUserProfile } from "../../../repositories/userRepository";
import { resolvePlayer } from "../../identityService";
import { calculateProfileCompletion } from "../../../domain/identity/profileCompletion";
import { readPendingRosterInviteRoom } from "../../../domain/auth/rosterJoinSession";
import { analyticsService, AnalyticsEventCatalog } from "../index";

const RETURNING_USER_STORAGE_KEY = "tra_analytics_returning_v1";

/**
 * @param {import('firebase/auth').User} user
 */
export function resolveAuthProviderForAnalytics(user) {
  if (!user || user.isAnonymous) return "anonymous";
  if (userHasGoogleProvider(user)) return "google";
  if (userHasPasswordProvider(user)) return "email";
  return "email";
}

function readReturningUserFlag() {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(RETURNING_USER_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function markReturningUserFlag() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(RETURNING_USER_STORAGE_KEY, "1");
  } catch {
    /* storage */
  }
}

/**
 * @param {import('firebase/auth').User} user
 * @param {Record<string, unknown> | null | undefined} [userProfile]
 */
async function loadPlayerForAnalytics(user, userProfile) {
  const profile = userProfile || (await fetchUserProfile(user.uid));
  const playerId =
    profile && typeof profile.playerId === "string" ? profile.playerId : null;
  if (!playerId) return { profile, player: null };
  const player = await resolvePlayer(playerId);
  return { profile, player };
}

/**
 * @param {import('firebase/auth').User} user
 * @param {import('../../../domain/identity/playerTypes').PlayerRecord | null} player
 */
function buildUserProperties(user, player) {
  const authProvider = resolveAuthProviderForAnalytics(user);
  const completion = player
    ? calculateProfileCompletion(player, false)
    : { percentage: 0 };

  /** @type {import('../analytics.types').AnalyticsUserProperties} */
  const props = {
    account_type: user.isAnonymous ? "anonymous" : "registered",
    auth_provider: authProvider,
    sport_module: "badminton",
    profile_completed: completion.percentage >= 100,
    returning_user: readReturningUserFlag(),
  };

  const countryIso =
    player && typeof player.countryIso === "string"
      ? player.countryIso.trim().toUpperCase()
      : "";
  if (countryIso.length === 2) {
    props.country = countryIso;
  }

  return props;
}

/**
 * @param {import('firebase/auth').User} user
 * @param {Record<string, unknown> | null | undefined} [userProfile]
 */
export async function applyAuthAnalyticsIdentity(user, userProfile) {
  if (!user?.uid || user.isAnonymous) return;

  const { profile, player } = await loadPlayerForAnalytics(user, userProfile);
  const playerId =
    player?.playerId ||
    (typeof profile?.playerId === "string" ? profile.playerId : null);

  analyticsService.identify({
    playerId: playerId || undefined,
    uid: user.uid,
  });
  analyticsService.setUserProperties(buildUserProperties(user, player));
}

/**
 * @param {import('firebase/auth').User} user
 * @param {{ signupMethod?: 'welcome' | 'invite' }} [options]
 */
export async function trackSignupCompleted(user, options = {}) {
  if (!user || user.isAnonymous) return;

  const signupMethod =
    options.signupMethod ||
    (readPendingRosterInviteRoom() ? "invite" : "welcome");

  analyticsService.track(AnalyticsEventCatalog.SIGNUP_COMPLETED, {
    auth_provider: resolveAuthProviderForAnalytics(user),
    signup_method: signupMethod,
  });

  await applyAuthAnalyticsIdentity(user);
  markReturningUserFlag();
}

/**
 * @param {import('firebase/auth').User} user
 * @param {Record<string, unknown> | null | undefined} [userProfile]
 */
export async function trackLogin(user, userProfile) {
  if (!user || user.isAnonymous) return;

  const isReturning = readReturningUserFlag();
  analyticsService.track(AnalyticsEventCatalog.LOGIN, {
    auth_provider: resolveAuthProviderForAnalytics(user),
    is_returning: isReturning,
  });

  markReturningUserFlag();
  await applyAuthAnalyticsIdentity(user, userProfile);
}

export function trackLogout() {
  analyticsService.track(AnalyticsEventCatalog.LOGOUT, {});
}

/**
 * @param {{ deletionReasonCode?: 'retain' | 'erase' }} [params]
 */
export function trackAccountDeleted(params = {}) {
  /** @type {import("../analytics.types").AnalyticsEventParams} */
  const payload = {};
  if (params.deletionReasonCode) {
    payload.deletion_reason_code = params.deletionReasonCode;
  }
  analyticsService.track(AnalyticsEventCatalog.ACCOUNT_DELETED, payload);
}

/**
 * Google popup / credential sign-in (explicit user action).
 * @param {import('firebase/auth').UserCredential} credential
 * @param {Record<string, unknown> | null | undefined} [userProfile]
 */
export async function trackAuthAfterGoogleCredential(credential, userProfile) {
  const user = credential.user;
  const isNewUser = Boolean(getAdditionalUserInfo(credential)?.isNewUser);
  if (isNewUser) {
    await trackSignupCompleted(user, {
      signupMethod: readPendingRosterInviteRoom() ? "invite" : "welcome",
    });
    return;
  }
  await trackLogin(user, userProfile);
}
